// "Data item" picker — body-only content for the @symbo.ls/windows custom
// layer (id 'cvEntityPicker'), opened from the canvas create island.
//
// Search → `sdk.execute('search', 'workspace', { query, limit: 20 })` through
// `el.call('searchCanvasEntities', …)`; picking a row adds an entity node at
// the viewport centre. The same shape as LibraryPickerBody (ModalHeader +
// sticky search header + scrolling results), with ONE deliberate difference:
// the query and the results live on THIS COMPONENT'S state, not on root.
//
// Why local state: smbls 3.14 DROPS writes to undeclared root keys, and this
// package's root-state declaration (`state.js`) is only the standalone canvas
// app's — the same picker also runs inside the workspace shell, whose root
// state this package does not own. A component-local slice cannot be dropped
// by either host and needs no cross-package declaration.

export const CvEntityPickerRow = {
  tag: 'button',
  type: 'button',
  width: '100%',
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  padding: 'Y Z',
  round: 'radiusControl',
  borderWidth: '0',
  background: 'transparent',
  textAlign: 'left',
  color: 'title',
  cursor: 'pointer',
  ':hover': { background: 'hover' },
  ':focus-visible': { outline: '2px solid currentColor', outlineOffset: '2px' },

  Icon: {
    boxSize: 'A1 A1',
    color: 'caption',
    name: (el, s) => el.call('entityRefIcon', s.key || '')
  },
  Info: {
    flow: 'y',
    // 'W' (~2.5px) — the spacing rung nearest the hairline gap this row
    // wants. A raw px literal is an FA304 violation and the scale (base 17,
    // phi) has a rung for it: W sits one step below X.
    gap: 'W',
    flex: '1',
    minWidth: '0',
    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.title || s.key
    },
    Snippet: {
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.snippet || ''
    }
  },
  // The surface the hit came from ('documents', 'mail', …) — a code-ish
  // routing identifier the search endpoint returns, not UI copy.
  Surface: {
    tag: 'span',
    fontSize: 'Z',
    color: 'caption',
    whiteSpace: 'nowrap',
    text: (el, s) => s.surface || ''
  },

  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    el.call('addCanvasEntityItem', s.key)
    el.call('closeModal', 'cvEntityPicker')
  }
}

export const CvEntityPickerBody = {
  position: 'relative',
  // Fills its windows-kit window: the opener sizes the window (overlayMd,
  // the same as the library and integration pickers).
  width: '100%',
  maxHeight: '60vh',
  theme: 'dialog',
  round: 'radiusSheet',
  shadow: 'overlay',
  flow: 'y',
  overflow: 'hidden',
  padding: 'B',

  state: { query: '', rows: [], loading: false, searched: false },

  ModalHeader: {
    Hgroup: {
      H: { text: '{{ canvas.entity.picker.title | polyglot }}' },
      P: { text: '{{ canvas.entity.picker.subtitle | polyglot }}' }
    }
  },

  Header: {
    padding: 'A 0',
    flow: 'y',
    gap: 'V',
    background: 'overlay',
    SearchInput: {
      tag: 'input',
      type: 'text',
      placeholder: (el) => el.call('polyglot', 'canvas.entity.picker.placeholder'),
      theme: 'field',
      minHeight: 'controlH',
      padding: '0 Z2',
      round: 'radiusControl',
      border: 'hairline solid line',
      color: 'title',
      fontSize: 'Z2',
      value: (el, s) => s.query || '',
      onRender: (el) => {
        if (el.__autofocused) return
        el.__autofocused = true
        setTimeout(() => el.node?.focus?.(), 50)
      },
      // Debounced through `el.scope` (non-reactive per-instance storage — the
      // documented home for a timer handle). Typing writes the query
      // immediately so the field stays controlled; only the network call waits.
      onInput: (e, el, s) => {
        const value = el.node.value
        s.update({ query: value })
        if (el.scope.timer) clearTimeout(el.scope.timer)
        el.scope.timer = setTimeout(() => {
          el.scope.timer = null
          if (!el.node) return
          s.update({ loading: true })
          Promise.resolve(el.call('searchCanvasEntities', value))
            .then((rows) => {
              if (!el.node) return
              s.update({ rows: rows || [], loading: false, searched: true })
            })
            .catch(() => {
              if (!el.node) return
              s.update({ rows: [], loading: false, searched: true })
            })
        }, 250)
      }
    }
  },

  Results: {
    flow: 'y',
    gap: 'V',
    overflow: 'auto',
    flexGrow: '1',
    padding: 'V',
    childExtends: 'CvEntityPickerRow',
    childrenAs: 'state',
    children: (el, s) => (Array.isArray(s.rows) ? s.rows : [])
  },

  // Kept MOUNTED and toggled by visibility rather than `if:`-gated, so the
  // panel's height does not jump between "searching" and "no results"
  // (Rule 66 — chrome must never move what is already on screen).
  Empty: {
    tag: 'p',
    margin: '0',
    padding: 'Z A B',
    fontSize: 'Z1',
    color: 'caption',
    text: '{{ canvas.entity.picker.empty | polyglot }}',
    visibility: (el, s) => (s.searched && !s.loading && !s.rows.length ? 'visible' : 'hidden'),
    height: (el, s) => (s.searched && !s.loading && !s.rows.length ? 'auto' : '0'),
    overflow: 'hidden'
  }
}
